/**
 * Export Utility - Generate static HTML from CraftJS content
 * Uses React-based exportRenderer for accurate rendering (matches Editor/Preview)
 */
import { BlobWriter, ZipWriter, TextReader, BlobReader } from "@zip.js/zip.js";
import { PageData, ThemeSettings } from "../types";
import { generateStaticPage } from "./exportRenderer";

// Default theme if none provided
const defaultTheme: ThemeSettings = {
  primary: "#3B82F6",
  secondary: "#10B981",
  font: "Sarabun",
};

/**
 * Generate callback.html for LINE Login
 */
const generateCallbackHtml = (siteName: string, theme: ThemeSettings): string => {
  return `<!DOCTYPE html>
<html lang="th">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>กำลังเข้าสู่ระบบ... - ${siteName}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: '${theme.font}', -apple-system, BlinkMacSystemFont, sans-serif;
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
    }
    .callback-container {
      text-align: center;
      padding: 40px;
      background: white;
      border-radius: 16px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.1);
      max-width: 400px;
      width: 90%;
    }
    .spinner {
      width: 50px;
      height: 50px;
      border: 4px solid #e5e7eb;
      border-top-color: #06C755;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 20px;
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    h1 {
      font-size: 1.25rem;
      color: #1f2937;
      margin-bottom: 8px;
    }
    p {
      color: #6b7280;
      font-size: 0.875rem;
    }
    .error {
      color: #dc2626;
      margin-top: 16px;
      display: none;
    }
  </style>
</head>
<body>
  <div class="callback-container">
    <div class="spinner"></div>
    <h1>กำลังเข้าสู่ระบบ...</h1>
    <p>กรุณารอสักครู่</p>
    <p class="error" id="error-message"></p>
  </div>

  <script src="https://static.line-scdn.net/liff/edge/2/sdk.js"></script>
  <script>
    // Cookie functions
    function setCookie(name, value, days) {
      var expires = '';
      if (days) {
        var date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        expires = '; expires=' + date.toUTCString();
      }
      document.cookie = name + '=' + (value || '') + expires + '; path=/';
    }

    function showError(message) {
      document.querySelector('h1').textContent = 'เกิดข้อผิดพลาด';
      document.querySelector('.spinner').style.display = 'none';
      var errorEl = document.getElementById('error-message');
      errorEl.textContent = message;
      errorEl.style.display = 'block';
    }

    function clearLineSessionStorage() {
      sessionStorage.removeItem('line-liff-id');
      sessionStorage.removeItem('line-api-host');
      sessionStorage.removeItem('line-app-name');
      sessionStorage.removeItem('line-return-url');
    }

    // Main callback logic
    (function() {
      var liffId = sessionStorage.getItem('line-liff-id');
      var apiHost = sessionStorage.getItem('line-api-host');
      var appName = sessionStorage.getItem('line-app-name');
      var returnUrl = sessionStorage.getItem('line-return-url') || '/';

      if (!liffId) {
        showError('ไม่พบข้อมูล LIFF ID กรุณาลองใหม่อีกครั้ง');
        setTimeout(function() { window.location.href = '/'; }, 3000);
        return;
      }

      liff.init({ liffId: liffId }).then(function() {
        if (!liff.isLoggedIn()) {
          showError('การเข้าสู่ระบบไม่สำเร็จ');
          setTimeout(function() { window.location.href = returnUrl; }, 3000);
          return;
        }

        // Get user profile
        liff.getProfile().then(function(profile) {
          setCookie('line-user-id', profile.userId, 7);
          setCookie('line-display-name', profile.displayName, 7);
          if (profile.pictureUrl) {
            setCookie('line-picture-url', profile.pictureUrl, 7);
          }

          // Call API if apiHost is set
          if (apiHost) {
            var idToken = liff.getIDToken();
            fetch(apiHost + '/api/line/verify', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ idToken: idToken, appName: appName })
            }).then(function(res) {
              return res.json();
            }).then(function(data) {
              if (data.token) {
                setCookie('line-api-token', data.token, 7);
              }
              clearLineSessionStorage();
              window.location.href = returnUrl;
            }).catch(function(err) {
              console.error('API Error:', err);
              clearLineSessionStorage();
              window.location.href = returnUrl;
            });
          } else {
            clearLineSessionStorage();
            window.location.href = returnUrl;
          }
        }).catch(function(err) {
          console.error('Profile Error:', err);
          showError('ไม่สามารถดึงข้อมูลผู้ใช้ได้');
          clearLineSessionStorage();
          setTimeout(function() { window.location.href = returnUrl; }, 3000);
        });
      }).catch(function(err) {
        console.error('LIFF Init Error:', err);
        showError('เกิดข้อผิดพลาดในการเชื่อมต่อ LINE');
        clearLineSessionStorage();
        setTimeout(function() { window.location.href = returnUrl; }, 3000);
      });
    })();
  </script>
</body>
</html>`;
};

// Helper function to fetch image as blob
const fetchImageAsBlob = async (url: string): Promise<Blob | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    return await response.blob();
  } catch {
    console.warn(`Failed to fetch image: ${url}`);
    return null;
  }
};

// Social media images to include in export
const SOCIAL_IMAGES = [
  { path: "static/images/social/facebook.png", name: "facebook.png" },
  { path: "static/images/social/ig.png", name: "ig.png" },
  { path: "static/images/social/line.png", name: "line.png" },
  { path: "static/images/social/tiktok.png", name: "tiktok.png" },
  { path: "static/images/social/linked.png", name: "linked.png" },
  { path: "static/images/social/youtube.png", name: "youtube.png" },
  { path: "static/images/social/twitter.png", name: "twitter.png" },
];

export interface ExportOptions {
  password?: string;
  favicon?: string; // URL to favicon image
  logo?: string; // URL to logo image
  lineLiffId?: string; // LINE LIFF ID for floating chat bubble
  officialAccountId?: number; // LINE Official Account ID
  facebookUrl?: string; // Facebook page URL for floating button
  gaMeasurementId?: string; // GA4 Measurement ID (G-XXXXXXXXXX) for gtag injection
}

export const exportToZip = async (
  pages: PageData[],
  siteName: string,
  theme?: ThemeSettings,
  options?: ExportOptions
): Promise<Blob> => {
  const usedTheme = theme || defaultTheme;
  const password = options?.password;

  // Create zip writer with optional password (ZipCrypto encryption for wider compatibility)
  const blobWriter = new BlobWriter("application/zip");
  const zipWriter = new ZipWriter(blobWriter, {
    password: password || undefined,
    zipCrypto: !!password, // Use traditional ZipCrypto instead of AES for server compatibility
  });

  // Determine favicon filename if provided
  const faviconFilename = options?.favicon
    ? `favicon.${options.favicon.match(/\.(ico|png|jpg|jpeg|svg)$/i)?.[1] || "ico"}`
    : undefined;

  // Generate HTML for each page using React-based renderer (matches Editor/Preview exactly)
  for (const page of pages) {
    const pageTheme = page.theme || usedTheme;
    const html = generateStaticPage(page.content, pageTheme, page.name, siteName, page.seo, {
      favicon: faviconFilename,
      lineLiffId: options?.lineLiffId,
      officialAccountId: options?.officialAccountId,
      facebookUrl: options?.facebookUrl,
      gaMeasurementId: options?.gaMeasurementId,
    });
    const fileName = page.slug === "home" ? "index.html" : `${page.slug}.html`;
    await zipWriter.add(fileName, new TextReader(html));
  }

  // Generate callback.html for LINE Login
  const callbackHtml = generateCallbackHtml(siteName, usedTheme);
  await zipWriter.add("callback.html", new TextReader(callbackHtml));

  // Add social media images to zip
  for (const img of SOCIAL_IMAGES) {
    const blob = await fetchImageAsBlob(`/${img.path}`);
    if (blob) {
      await zipWriter.add(`static/images/social/${img.name}`, new BlobReader(blob));
    }
  }

  // Add favicon if provided
  if (options?.favicon) {
    try {
      const faviconBlob = await fetchImageAsBlob(options.favicon);
      if (faviconBlob) {
        // Determine file extension from URL or default to .ico
        const faviconExt = options.favicon.match(/\.(ico|png|jpg|jpeg|svg)$/i)?.[1] || "ico";
        await zipWriter.add(`favicon.${faviconExt}`, new BlobReader(faviconBlob));
      }
    } catch (err) {
      console.warn("Failed to add favicon to ZIP:", err);
    }
  }

  // Add logo if provided
  if (options?.logo) {
    try {
      const logoBlob = await fetchImageAsBlob(options.logo);
      if (logoBlob) {
        // Determine file extension from URL
        const logoExt = options.logo.match(/\.(png|jpg|jpeg|svg|webp)$/i)?.[1] || "png";
        await zipWriter.add(`static/images/logo.${logoExt}`, new BlobReader(logoBlob));
      }
    } catch (err) {
      console.warn("Failed to add logo to ZIP:", err);
    }
  }

  // Create a simple CSS file
  const cssContent = `
/* Theme Colors */
:root {
  --color-primary: ${usedTheme.primary};
  --color-secondary: ${usedTheme.secondary};
  --font-family: '${usedTheme.font}', sans-serif;
}
`;
  await zipWriter.add("styles.css", new TextReader(cssContent));

  // Create README
  const readmeContent = `# ${siteName}

This website was exported from ClinicCMS.

## Files
${pages.map((p) => `- ${p.slug === "home" ? "index.html" : `${p.slug}.html`} - ${p.name}`).join("\n")}
- callback.html - LINE Login callback handler
- static/images/social/ - Social media icons

## How to use
1. Upload all files to your web hosting
2. Make sure index.html is in the root directory
3. Your website is ready!

## LINE Login Setup
1. Go to LINE Developers Console
2. Add your callback URL: https://your-domain.com/callback
3. Configure LIFF ID in the CMS editor

Generated on: ${new Date().toISOString()}
${password ? "\n⚠️ This ZIP file is password protected." : ""}
`;
  await zipWriter.add("README.md", new TextReader(readmeContent));

  // Close and get blob
  const blob = await zipWriter.close();
  return blob;
};

// Re-export the new React-based renderer
export { generateStaticPage as generateReactPage } from "./exportRenderer";

export default exportToZip;
